import { useSearchParams } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Filter, X } from "lucide-react";
import { useMemo } from "react";

export type SchoolFilterState = {
  grade: string; // "all" | "8" | "9" | "10" | "11" | "12"
  house: string; // "all" | Sequoia | Redwood | Cedar | Willow
  term: string;  // "current" | "prev" | "ytd"
};

export const HOUSES = ["Sequoia", "Redwood", "Cedar", "Willow"] as const;
export const GRADES = ["8", "9", "10", "11", "12"] as const;

const TERM_LABELS: Record<string, string> = {
  current: "Term 2 · 2026",
  prev: "Term 1 · 2026",
  ytd: "Year to date",
};

/**
 * URL-synced grade / house / term filter bar for WeKIT Education Intelligence™ dashboards.
 * Values are surfaced via `useSchoolFilters()` so any page can react.
 */
export function useSchoolFilters(): [SchoolFilterState, (patch: Partial<SchoolFilterState>) => void, () => void] {
  const [params, setParams] = useSearchParams();
  const state: SchoolFilterState = {
    grade: params.get("grade") ?? "all",
    house: params.get("house") ?? "all",
    term:  params.get("term")  ?? "current",
  };
  const update = (patch: Partial<SchoolFilterState>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (!v || v === "all" || (k === "term" && v === "current")) next.delete(k);
      else next.set(k, v as string);
    });
    setParams(next, { replace: true });
  };
  const reset = () => {
    const next = new URLSearchParams(params);
    ["grade", "house", "term"].forEach((k) => next.delete(k));
    setParams(next, { replace: true });
  };
  return [state, update, reset];
}

export default function SchoolFilters({
  showHouse = true,
  showGrade = true,
  showTerm = true,
}: {
  showHouse?: boolean;
  showGrade?: boolean;
  showTerm?: boolean;
}) {
  const [state, update, reset] = useSchoolFilters();
  const active = useMemo(
    () => (state.grade !== "all" ? 1 : 0) + (state.house !== "all" ? 1 : 0) + (state.term !== "current" ? 1 : 0),
    [state]
  );

  return (
    <div
      role="region"
      aria-label="Dashboard filters"
      className="flex flex-wrap items-center gap-2 rounded-2xl border bg-card/60 backdrop-blur-sm px-3 py-2"
    >
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground pr-1">
        <Filter className="w-3.5 h-3.5" aria-hidden="true" />
        <span className="uppercase tracking-wider">Filters</span>
      </div>

      {showGrade && (
        <Select value={state.grade} onValueChange={(v) => update({ grade: v })}>
          <SelectTrigger className="h-8 w-[130px] text-xs" aria-label="Filter by grade">
            <SelectValue placeholder="Grade" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All grades</SelectItem>
            {GRADES.map((g) => (
              <SelectItem key={g} value={g}>Grade {g}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {showHouse && (
        <Select value={state.house} onValueChange={(v) => update({ house: v })}>
          <SelectTrigger className="h-8 w-[130px] text-xs" aria-label="Filter by house">
            <SelectValue placeholder="House" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All houses</SelectItem>
            {HOUSES.map((h) => (
              <SelectItem key={h} value={h}>{h}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {showTerm && (
        <Select value={state.term} onValueChange={(v) => update({ term: v })}>
          <SelectTrigger className="h-8 w-[150px] text-xs" aria-label="Filter by term">
            <SelectValue placeholder="Term" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="current">{TERM_LABELS.current}</SelectItem>
            <SelectItem value="prev">{TERM_LABELS.prev}</SelectItem>
            <SelectItem value="ytd">{TERM_LABELS.ytd}</SelectItem>
          </SelectContent>
        </Select>
      )}

      {active > 0 && (
        <Button
          size="sm"
          variant="ghost"
          onClick={reset}
          className="h-8 text-xs"
          aria-label="Reset all filters"
        >
          <X className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Reset
        </Button>
      )}
    </div>
  );
}

export function termLabel(term: string) {
  return TERM_LABELS[term] ?? TERM_LABELS.current;
}
